<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import Dropdown from 'primevue/dropdown';
import Calendar from 'primevue/calendar';
import AutoComplete from 'primevue/autocomplete';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { PERMISSIONS } from '@/constants/permissions';
import { useAuthStore } from '@/stores/authStore';
import { useSocketStore } from '@/stores/socketStore';
import { useTimeTrackingStore } from '@/stores/timeTrackingStore';
import { PhotoService } from '@/services/PhotoService';
import TimeTrackingService from '@/services/TimeTrackingService';
import type { Photo } from '@/types/photos';
import BuildTimeTrackerSlideover from '@/components/powerpacks/BuildTimeTrackerSlideover.vue';
import {
  BuildPositionResponse,
  completeBuild,
  createBuildPosition,
  createBuildPositionsBulk,
  deleteBuildPosition,
  fetchBuildPositions,
  updateBuildRailcar,
  updateBuildCablesHooks,
  updateBuildFuelEngineHours,
  updateBuildTimesNotes,
  reorderBuildPositions,
  downloadBuildReport,
  fetchRecentYardCheckUnits
} from '@/services/PowerPackBuildService';
import { FUEL_OPTIONS } from '@/utilities/constants';
import BatchAddBuildPositionsDialog from '@/components/powerpacks/BatchAddBuildPositionsDialog.vue';
import EditBuildTimeRecordDialog from '@/components/powerpacks/EditBuildTimeRecordDialog.vue';
import AddBuildTimeRecordDialog from '@/components/powerpacks/AddBuildTimeRecordDialog.vue';

interface BuildSummary {
  build_id: number;
  genset_number: string;
  track_id: string | null;
  railcar: string | null;
  num_cables?: number | null;
  num_hooks?: number | null;
  fuel_level?: string | null;
  g1_hours?: number | null;
  g2_hours?: number | null;
  call_in_time: string | null;
  start_time: string | null;
  end_time?: string | null;
  delays?: string | null;
  repairs_required?: string | null;
  positions_count?: number;
  completed_at?: string | null;
  completed_by?: number | null;
  completed_by_first_name?: string | null;
  completed_by_last_name?: string | null;
}

const props = defineProps<{
  visible: boolean;
  build: BuildSummary | null;
}>();

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'positionsChanged'): void;
}>();

const toast = useToast();
const confirm = useConfirm();
const authStore = useAuthStore();
const socketStore = useSocketStore();
const timeTrackingStore = useTimeTrackingStore();

const positions = ref<BuildPositionResponse[]>([]);
const loadingPositions = ref(false);
const submitting = ref(false);
const deletingId = ref<number | null>(null);
const bulkDialogVisible = ref(false);
const uploadingPhoto = ref(false);
const libraryFileInput = ref<HTMLInputElement | null>(null);
const cameraFileInput = ref<HTMLInputElement | null>(null);
const railcarInput = ref('');
const savingRailcar = ref(false);
const numCablesInput = ref<number | null>(null);
const numHooksInput = ref<number | null>(null);
const savingCablesHooks = ref(false);
const fuelLevelInput = ref<string | null>(null);
const g1HoursInput = ref<number | null>(null);
const g2HoursInput = ref<number | null>(null);
const savingFuelEngineHours = ref(false);
const endTimeInput = ref<Date | null>(null);
const delaysInput = ref<string | null>(null);
const repairsRequiredInput = ref<string | null>(null);
const savingTimesNotes = ref(false);
const reorderingPositions = ref(false);
const unitSuggestions = ref<string[]>([]);
const filteredUnitSuggestions = ref<string[]>([]);
const loadingSuggestions = ref(false);

// Time tracking state
const showTimeTracker = ref(false);
const pendingTimerOperation = ref<Promise<void> | null>(null);

// Time records management state
interface TimeRecord {
  id: number;
  first_name?: string;
  last_name?: string;
  start_time?: string;
  end_time?: string;
  user_id?: number;
}
const timeRecords = ref<TimeRecord[]>([]);
const loadingTimeRecords = ref(false);
const editTimeRecordDialogVisible = ref(false);
const addTimeRecordDialogVisible = ref(false);
const selectedTimeRecord = ref<TimeRecord | null>(null);
const deletingTimeRecordId = ref<number | null>(null);

const form = reactive({
  unitNumber: '',
  carNumber: '',
  well: '',
  position: 'Top',
  photoUrl: '',
  runningTempC: '' as string,
  billedSetpointC: '' as string,
  actualSetpointC: '' as string,
  comments: '' as string,
});

const lockedFields = reactive({
  carNumber: false,
  well: false,
  position: false
});

const wellOptions = ['A', 'B', 'C', 'D', 'E'];
const positionOptions = [
  { label: 'Top', value: 'Top' },
  { label: 'Bottom', value: 'Bottom' }
];

const canEdit = computed(() => authStore.hasPermission(PERMISSIONS.POWER_PACKS_UPDATE));

// Permission check for time record management (admins, managers, supervisors, dispatchers)
const canManageTimeRecords = computed(() =>
  authStore.hasAnyPermission([PERMISSIONS.TIME_APPROVE, PERMISSIONS.TIME_DELETE])
);

const buildId = computed(() => props.build?.build_id ?? null);
const isCompleted = computed(() => !!props.build?.completed_at);

// Time tracking computed properties
const currentUserTimeRecord = computed(() => {
  return timeTrackingStore.activeTimeRecords.find(record => {
    if (record.type === 'Build' || record.recordDetails?.buildId) {
      const recordBuildId = record.recordDetails?.buildId;
      return (
        (Number(recordBuildId) === Number(buildId.value)) &&
        record.isStartedByCurrentUser
      );
    }
    return false;
  });
});

const currentUserElapsedSeconds = computed(() => {
  if (currentUserTimeRecord.value) {
    return currentUserTimeRecord.value.elapsedSeconds || 0;
  }
  return 0;
});

const isCurrentUserTracking = computed(() => {
  return !!currentUserTimeRecord.value;
});

const formatElapsedTime = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Time tracking methods
const startBuildTime = async () => {
  if (!buildId.value) return;

  try {
    if (pendingTimerOperation.value) {
      await pendingTimerOperation.value;
    }

    let resolveOperation: () => void;
    pendingTimerOperation.value = new Promise(resolve => {
      resolveOperation = resolve;
    });

    const response = await TimeTrackingService.startBuildTime(buildId.value);

    if (response?.success === false && response?.error) {
      if (response.error.code === 'ACTIVE_TIME_EXISTS') {
        toast.add({
          severity: 'warn',
          summary: 'Already Tracking Time',
          detail: response.error.message || 'You already have an active time record on another build',
          life: 5000
        });
      } else {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: response.error.message || 'Failed to start time',
          life: 3000
        });
      }
      resolveOperation!();
      pendingTimerOperation.value = null;
      return;
    }

    toast.add({
      severity: 'success',
      summary: 'Started',
      detail: 'Time tracking started',
      life: 3000
    });

    // Refresh time records
    await timeTrackingStore.fetchActiveTimeRecords();
    timeTrackingStore.startTimer();

    resolveOperation!();
    pendingTimerOperation.value = null;
  } catch (error: any) {
    pendingTimerOperation.value = null;
    console.error('Error starting build time:', error);

    const errorMessage = error.response?.data?.error?.message || 'Failed to start time';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage,
      life: 3000
    });
  }
};

const stopBuildTime = async () => {
  if (!buildId.value) return;

  try {
    if (pendingTimerOperation.value) {
      await pendingTimerOperation.value;
    }

    let resolveOperation: () => void;
    pendingTimerOperation.value = new Promise(resolve => {
      resolveOperation = resolve;
    });

    await TimeTrackingService.endBuildTime(buildId.value);

    toast.add({
      severity: 'success',
      summary: 'Stopped',
      detail: 'Time tracking stopped',
      life: 3000
    });

    // Refresh time records
    await timeTrackingStore.fetchActiveTimeRecords();

    resolveOperation!();
    pendingTimerOperation.value = null;
  } catch (error: any) {
    pendingTimerOperation.value = null;
    console.error('Error stopping build time:', error);

    const errorMessage = error.response?.data?.error?.message || 'Failed to stop time';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage,
      life: 3000
    });
  }
};

// Socket event handler for build time updates
const handleBuildTimeUpdate = (data: any) => {
  console.log('Received buildTimeTracking:updated event:', data);
  if (data?.buildId === buildId.value) {
    void timeTrackingStore.fetchActiveTimeRecords();
  }
};

const toggleLock = (field: 'carNumber' | 'well' | 'position') => {
  lockedFields[field] = !lockedFields[field];
};

const resetForm = () => {
  form.unitNumber = '';
  if (!lockedFields.carNumber) {
    form.carNumber = '';
  }
  if (!lockedFields.well) {
    form.well = '';
  }
  if (!lockedFields.position) {
    form.position = 'Top';
  }
  form.photoUrl = '';
  form.runningTempC = '';
  form.billedSetpointC = '';
  form.actualSetpointC = '';
  form.comments = '';
};

type PhotoSource = 'library' | 'camera';

const parseTimestampRaw = (value: string): Date | null => {
  if (!value) {
    return null;
  }

  let normalized = value.trim();
  if (!normalized) {
    return null;
  }

  if (!normalized.includes('T')) {
    normalized = normalized.replace(' ', 'T');
  }

  normalized = normalized.replace(/\.(\d{3})\d+/, '.$1');

  normalized = normalized.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  normalized = normalized.replace(/([+-]\d{2})$/, '$1:00');

  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(normalized)) {
    normalized += 'Z';
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizePositionRow = (row: BuildPositionResponse): BuildPositionResponse => {
  const normalized: BuildPositionResponse = {
    ...row,
    position_id: Number(row.position_id),
    build_id: Number(row.build_id),
  };

  const parsed = parseTimestampRaw(row.created_at);
  if (parsed && parsed.getFullYear() >= 2000) {
    normalized.created_at = parsed.toISOString();
  } else {
    normalized.created_at = new Date().toISOString();
  }

  return normalized;
};

const formatRecordedAt = (value?: string | null) => {
  if (!value) {
    return new Date().toLocaleString('en-CA', { hourCycle: 'h23' });
  }

  const parsed = parseTimestampRaw(value) ?? new Date();
  return parsed.toLocaleString('en-CA', { hourCycle: 'h23' });
};

const resolvePhotoUrl = (photo: Photo): string | null => {
  return (
    photo.signedUrl ??
    photo.storageUrl ??
    photo.storage_url ??
    photo.thumbnailSignedUrl ??
    photo.thumbnail_storage_url ??
    null
  );
};

const triggerPhotoSelection = (mode: PhotoSource) => {
  if (uploadingPhoto.value) {
    return;
  }

  const normalizedUnit = form.unitNumber.trim().toUpperCase();
  if (!normalizedUnit) {
    toast.add({
      severity: 'warn',
      summary: 'Unit required',
      detail: 'Enter the unit number before attaching a photo.',
      life: 3500,
    });
    return;
  }

  form.unitNumber = normalizedUnit;

  const inputRef = mode === 'library' ? libraryFileInput.value : cameraFileInput.value;
  inputRef?.click();
};

const handlePhotoChange = async (mode: PhotoSource, event: Event) => {
  const input = event.target as HTMLInputElement | null;
  const files = input?.files;
  if (!files || files.length === 0) {
    return;
  }

  const file = files[0];
  const normalizedUnit = form.unitNumber.trim().toUpperCase();
  if (!normalizedUnit) {
    toast.add({
      severity: 'warn',
      summary: 'Unit required',
      detail: 'Enter the unit number before attaching a photo.',
      life: 3500,
    });
    input.value = '';
    return;
  }

  try {
    uploadingPhoto.value = true;
    const uploadedPhoto = await PhotoService.uploadPhotoForUnit(normalizedUnit, file);
    const photoUrl = resolvePhotoUrl(uploadedPhoto);

    if (photoUrl) {
      form.photoUrl = photoUrl;
      toast.add({
        severity: 'success',
        summary: 'Photo attached',
        detail: 'The photo was uploaded successfully.',
        life: 3000,
      });
    } else {
      toast.add({
        severity: 'warn',
        summary: 'Photo uploaded',
        detail: 'Photo uploaded but no link was returned. Please verify in Photos.',
        life: 4000,
      });
    }
  } catch (error) {
    console.error('Failed to upload photo for build position', error);
    toast.add({
      severity: 'error',
      summary: 'Upload failed',
      detail: 'We were unable to upload the photo. Try again or contact support.',
      life: 4000,
    });
  } finally {
    uploadingPhoto.value = false;
    if (input) {
      input.value = '';
    }
  }
};

const loadPositions = async () => {
  if (!buildId.value) {
    positions.value = [];
    return;
  }
  try {
    loadingPositions.value = true;
    deletingId.value = null;
    const fetched = await fetchBuildPositions(buildId.value);
    positions.value = fetched.map(normalizePositionRow);
    console.debug('Loaded build positions', { buildId: buildId.value, positions: positions.value });
  } catch (error) {
    console.error('Failed to load build positions', error);
    toast.add({
      severity: 'error',
      summary: 'Unable to load positions',
      detail: 'Please try again or contact support if the issue persists.'
    });
  } finally {
    loadingPositions.value = false;
    deletingId.value = null;
  }
};

const loadUnitSuggestions = async () => {
  try {
    loadingSuggestions.value = true;
    const units = await fetchRecentYardCheckUnits();
    unitSuggestions.value = units;
  } catch (error) {
    console.error('Failed to load unit suggestions', error);
    // Fail silently - the input will still work without suggestions
  } finally {
    loadingSuggestions.value = false;
  }
};

const searchUnits = (event: { query: string }) => {
  const query = event.query.trim().toUpperCase();
  if (!query) {
    filteredUnitSuggestions.value = unitSuggestions.value;
  } else {
    filteredUnitSuggestions.value = unitSuggestions.value.filter((unit) =>
      unit.toUpperCase().includes(query)
    );
  }
};

const handleRowReorder = async (event: any) => {
  if (!buildId.value || !event.value) {
    return;
  }

  try {
    reorderingPositions.value = true;

    // Update local positions array with new order
    const reorderedPositions = event.value as BuildPositionResponse[];
    positions.value = reorderedPositions;

    // Create reorder payload with new sequences
    const reorderPayload = reorderedPositions.map((pos, index) => ({
      position_id: pos.position_id,
      sequence: index + 1
    }));

    await reorderBuildPositions(buildId.value, reorderPayload);

    toast.add({
      severity: 'success',
      summary: 'Order updated',
      detail: 'Position order has been saved.',
      life: 2000
    });

    emit('positionsChanged');
  } catch (error: any) {
    console.error('Failed to reorder positions', error);
    toast.add({
      severity: 'error',
      summary: 'Failed to reorder',
      detail: error?.response?.data?.message || 'Unable to update position order.'
    });
    // Reload to restore correct order
    await loadPositions();
  } finally {
    reorderingPositions.value = false;
  }
};

const addPosition = async () => {
  if (!buildId.value) {
    return;
  }

  const unitNumber = form.unitNumber.trim().toUpperCase();
  const carNumber = form.carNumber.trim().toUpperCase();
  const well = form.well.trim().toUpperCase();
  const position = form.position;
  const photoUrl = form.photoUrl.trim() ? form.photoUrl.trim() : null;

  if (!unitNumber || !carNumber || !well || !position) {
    toast.add({ severity: 'warn', summary: 'Missing information', detail: 'Please fill all required fields.' });
    return;
  }

  const duplicateUnit = positions.value.some((entry) => entry.unit_number === unitNumber);
  if (duplicateUnit) {
    toast.add({ severity: 'error', summary: 'Duplicate unit', detail: 'This unit is already on the build.' });
    return;
  }

  const duplicateLocation = positions.value.some(
    (entry) =>
      entry.car_number === carNumber && entry.well === well && entry.position.toLowerCase() === position.toLowerCase()
  );
  if (duplicateLocation) {
    toast.add({ severity: 'error', summary: 'Duplicate location', detail: 'That car / well / position is already in use.' });
    return;
  }

  try {
    submitting.value = true;
    // Parse temps/setpoints if provided
    const toNum = (v: any): number | null => {
      if (v === undefined || v === null || v === '') return null;
      const n = parseFloat(String(v));
      return Number.isFinite(n) ? n : null;
    };
    const runningTempC = toNum(form.runningTempC);
    const billedSetpoint = toNum(form.billedSetpointC);
    const actualSetpoint = toNum(form.actualSetpointC);

    await createBuildPosition(buildId.value, {
      unitNumber,
      carNumber,
      well,
      position,
      photoUrl,
      runningTempC,
      billedSetpoint,
      actualSetpoint,
    });
    await loadPositions();
    deletingId.value = null;
    toast.add({ severity: 'success', summary: 'Position added', life: 2500 });
    resetForm();
    emit('positionsChanged');
  } catch (error: any) {
    console.error('Failed to add position', error);
    const detail = error?.response?.data?.detail || error?.response?.data?.message || 'Unable to add position.';
    toast.add({ severity: 'error', summary: 'Failed to add', detail });
  } finally {
    submitting.value = false;
  }
};

const toPositiveIntString = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const str = typeof value === 'number' ? String(Math.trunc(value)) : String(value).trim();
  if (!/^[0-9]+$/.test(str)) {
    return null;
  }

  return /^0+$/.test(str) ? null : str.replace(/^0+/, '') || '0';
};

const removePosition = (positionId: number, event: Event) => {
  const position = positions.value.find(p => p.position_id === positionId);
  if (!position) {
    return;
  }

  confirm.require({
    target: event.currentTarget as HTMLElement,
    message: `Remove ${position.unit_number} from this build?`,
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async () => {
      if (!buildId.value) {
        return;
      }
      try {
        const buildIdStr = toPositiveIntString(buildId.value);
        const positionIdStr = toPositiveIntString(positionId);

        if (!buildIdStr || !positionIdStr) {
          console.error('Invalid position identifier detected', {
            rawBuildId: buildId.value,
            rawPositionId: positionId,
            positionIdType: typeof positionId,
            buildIdStr,
            positionIdStr,
            isNaN: isNaN(positionId),
            numericValue: Number(positionId),
          });
          toast.add({ severity: 'error', summary: 'Failed to remove', detail: `Invalid position identifier: ${positionId}. Please reload the page.` });
          deletingId.value = null;
          return;
        }

        deletingId.value = Number(positionIdStr);
        await deleteBuildPosition(buildIdStr, positionIdStr);
        await loadPositions();
        deletingId.value = null;
        toast.add({ severity: 'success', summary: 'Position removed', life: 2000 });
        emit('positionsChanged');
      } catch (error) {
        console.error('Failed to delete position', error);
        const detail =
          (error as any)?.response?.data?.detail ||
          (error as any)?.response?.data?.message ||
          'Unable to remove position.';
        toast.add({ severity: 'error', summary: 'Failed to remove', detail });
      } finally {
        deletingId.value = null;
      }
    }
  });
};

const saveRailcar = async () => {
  if (!buildId.value) {
    return;
  }

  const trimmedRailcar = railcarInput.value.trim().toUpperCase();
  if (!trimmedRailcar) {
    toast.add({
      severity: 'warn',
      summary: 'Railcar required',
      detail: 'Please enter a railcar number.',
      life: 3000
    });
    return;
  }

  try {
    savingRailcar.value = true;
    await updateBuildRailcar(buildId.value, trimmedRailcar);

    // Parent will refresh data after positions-changed event
    toast.add({
      severity: 'success',
      summary: 'Railcar saved',
      detail: 'The railcar number has been updated.',
      life: 2500
    });

    emit('positionsChanged');
  } catch (error: any) {
    console.error('Failed to update railcar', error);
    const detail = error?.response?.data?.detail || error?.response?.data?.message || 'Unable to update railcar.';
    toast.add({ severity: 'error', summary: 'Failed to save', detail });
  } finally {
    savingRailcar.value = false;
  }
};

const saveCablesHooks = async () => {
  if (!buildId.value) {
    return;
  }

  try {
    savingCablesHooks.value = true;
    await updateBuildCablesHooks(buildId.value, numCablesInput.value, numHooksInput.value);

    toast.add({
      severity: 'success',
      summary: 'Saved',
      detail: 'Cables and hooks have been updated.',
      life: 2500
    });

    emit('positionsChanged');
  } catch (error: any) {
    console.error('Failed to update cables and hooks', error);
    const detail = error?.response?.data?.detail || error?.response?.data?.message || 'Unable to update cables and hooks.';
    toast.add({ severity: 'error', summary: 'Failed to save', detail });
  } finally {
    savingCablesHooks.value = false;
  }
};

const saveFuelEngineHours = async () => {
  if (!buildId.value) {
    return;
  }

  try {
    savingFuelEngineHours.value = true;
    await updateBuildFuelEngineHours(
      buildId.value,
      fuelLevelInput.value,
      g1HoursInput.value,
      g2HoursInput.value
    );

    toast.add({
      severity: 'success',
      summary: 'Saved',
      detail: 'Fuel level and engine hours have been updated.',
      life: 2500
    });

    emit('positionsChanged');
  } catch (error: any) {
    console.error('Failed to update fuel level and engine hours', error);
    const detail = error?.response?.data?.detail || error?.response?.data?.message || 'Unable to update fuel level and engine hours.';
    toast.add({ severity: 'error', summary: 'Failed to save', detail });
  } finally {
    savingFuelEngineHours.value = false;
  }
};

const saveTimesNotes = async () => {
  if (!buildId.value) {
    return;
  }

  try {
    savingTimesNotes.value = true;
    await updateBuildTimesNotes(
      buildId.value,
      endTimeInput.value ? endTimeInput.value.toISOString() : null,
      delaysInput.value,
      repairsRequiredInput.value
    );

    toast.add({
      severity: 'success',
      summary: 'Saved',
      detail: 'End time, delays, and repairs have been updated.',
      life: 2500
    });

    emit('positionsChanged');
  } catch (error: any) {
    console.error('Failed to update times and notes', error);
    const detail = error?.response?.data?.detail || error?.response?.data?.message || 'Unable to update end time, delays, and repairs.';
    toast.add({ severity: 'error', summary: 'Failed to save', detail });
  } finally {
    savingTimesNotes.value = false;
  }
};

const completingBuild = ref(false);

const handleCompleteBuild = async (event: Event) => {
  if (!buildId.value || !props.build) {
    return;
  }

  // Check if any time has been logged for this build
  let hasTimeRecords = false;
  try {
    const response = await TimeTrackingService.checkBuildHasTimeRecords(buildId.value);
    hasTimeRecords = response?.hasRecords === true;
  } catch (error) {
    console.warn('Could not check time records, proceeding anyway:', error);
    hasTimeRecords = true; // Assume records exist if check fails
  }

  // Build the confirmation message based on whether time was logged
  let confirmMessage = 'Mark this build as complete and send the report? This cannot be undone.';
  let confirmIcon = 'pi pi-check-circle';
  let acceptClass = 'p-button-success';

  if (!hasTimeRecords) {
    confirmMessage = '⚠️ No time has been logged for this build.\n\nAre you sure you want to complete it without any worker time records?';
    confirmIcon = 'pi pi-exclamation-triangle';
    acceptClass = 'p-button-warning';
  }

  confirm.require({
    target: event.currentTarget as HTMLElement,
    message: confirmMessage,
    icon: confirmIcon,
    acceptClass: acceptClass,
    accept: async () => {
      if (!buildId.value) {
        return;
      }
      try {
        completingBuild.value = true;
        await completeBuild(buildId.value);

        toast.add({
          severity: 'success',
          summary: 'Build completed',
          detail: 'The build has been marked complete and the report has been sent.',
          life: 4000
        });

        emit('positionsChanged');
      } catch (error: any) {
        console.error('Failed to complete build', error);
        const detail = error?.response?.data?.detail || error?.response?.data?.message || 'Unable to complete build.';
        toast.add({ severity: 'error', summary: 'Failed to complete', detail });
      } finally {
        completingBuild.value = false;
      }
    }
  });
};

const downloadingNew = ref(false);
const downloadingOriginal = ref(false);

const handleDownloadReport = async (format: 'new' | 'original') => {
  if (!buildId.value) {
    return;
  }

  if (format === 'new') {
    downloadingNew.value = true;
  } else {
    downloadingOriginal.value = true;
  }

  try {
    await downloadBuildReport(buildId.value, format);
    toast.add({
      severity: 'success',
      summary: 'Download started',
      detail: `Build report (${format} format) is downloading.`,
      life: 3000
    });
  } catch (error: any) {
    console.error('Failed to download build report:', error);
    const detail = error?.response?.data?.message || error?.message || 'Unable to download build report.';
    toast.add({ severity: 'error', summary: 'Download failed', detail });
  } finally {
    if (format === 'new') {
      downloadingNew.value = false;
    } else {
      downloadingOriginal.value = false;
    }
  }
};

// Time records management methods
const loadTimeRecords = async () => {
  if (!buildId.value) {
    timeRecords.value = [];
    return;
  }
  try {
    loadingTimeRecords.value = true;
    const response = await TimeTrackingService.getBuildTimeRecords(buildId.value);
    timeRecords.value = response?.data?.records || [];
  } catch (error) {
    console.error('Failed to load time records:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load time records',
      life: 3000
    });
  } finally {
    loadingTimeRecords.value = false;
  }
};

const handleEditTimeRecord = (record: TimeRecord) => {
  selectedTimeRecord.value = record;
  editTimeRecordDialogVisible.value = true;
};

const handleDeleteTimeRecord = (record: TimeRecord, event: Event) => {
  confirm.require({
    target: event.currentTarget as HTMLElement,
    message: `Delete time record for ${record.first_name} ${record.last_name}?`,
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        deletingTimeRecordId.value = record.id;
        await TimeTrackingService.deleteTimeRecord(record.id);
        toast.add({
          severity: 'success',
          summary: 'Deleted',
          detail: 'Time record deleted successfully',
          life: 3000
        });
        await loadTimeRecords();
      } catch (error: any) {
        console.error('Failed to delete time record:', error);
        const message = error.response?.data?.error?.message || 'Failed to delete time record';
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: message,
          life: 5000
        });
      } finally {
        deletingTimeRecordId.value = null;
      }
    }
  });
};

const refreshTimeRecords = () => {
  void loadTimeRecords();
};

// Format duration from start/end times
const formatDuration = (startTime?: string, endTime?: string): string => {
  if (!startTime) return '—';

  const start = new Date(startTime);
  const end = endTime ? new Date(endTime) : new Date();

  const diffMs = end.getTime() - start.getTime();
  if (diffMs < 0) return '—';

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

// Format datetime for display
const formatDateTime = (dateString?: string): string => {
  if (!dateString) return '(active)';
  return new Date(dateString).toLocaleString('en-CA', { hourCycle: 'h23' });
};

// Calculate total time for all records
const totalTimeSeconds = computed(() => {
  return timeRecords.value.reduce((total, record) => {
    if (!record.start_time) return total;

    const start = new Date(record.start_time);
    const end = record.end_time ? new Date(record.end_time) : new Date();

    const diffMs = end.getTime() - start.getTime();
    if (diffMs < 0) return total;

    return total + Math.floor(diffMs / 1000);
  }, 0);
});

const formattedTotalTime = computed(() => {
  const seconds = totalTimeSeconds.value;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
});

watch(
  () => props.visible,
  async (visible) => {
    if (visible) {
      console.debug('Opening ManageBuildPositionsDialog', { build: props.build });
      railcarInput.value = props.build?.railcar || '';
      numCablesInput.value = props.build?.num_cables ?? null;
      numHooksInput.value = props.build?.num_hooks ?? null;
      fuelLevelInput.value = props.build?.fuel_level ?? null;
      g1HoursInput.value = props.build?.g1_hours ?? null;
      g2HoursInput.value = props.build?.g2_hours ?? null;
      endTimeInput.value = props.build?.end_time ? new Date(props.build.end_time) : null;
      delaysInput.value = props.build?.delays ?? null;
      repairsRequiredInput.value = props.build?.repairs_required ?? null;
      void loadPositions();
      resetForm();

      // Load time records for managers to view/edit
      if (canManageTimeRecords.value) {
        void loadTimeRecords();
      }

      // Fetch time tracking state and start timer
      await timeTrackingStore.fetchActiveTimeRecords();
      timeTrackingStore.startTimer();

      // Join build room for socket updates
      if (socketStore.socket && buildId.value) {
        socketStore.socket.emit('joinBuild', buildId.value);
      }
    } else {
      // Leave build room when dialog closes
      if (socketStore.socket && buildId.value) {
        socketStore.socket.emit('leaveBuild', buildId.value);
      }
    }
  }
);

watch(
  () => props.build?.build_id,
  () => {
    if (props.visible) {
      void loadPositions();
    }
  }
);

onMounted(async () => {
  if (props.visible) {
    void loadPositions();
    // Fetch time tracking state
    await timeTrackingStore.fetchActiveTimeRecords();
    timeTrackingStore.startTimer();
  }
  void loadUnitSuggestions();

  // Listen for socket updates
  if (socketStore.socket) {
    socketStore.socket.on('buildTimeTracking:updated', handleBuildTimeUpdate);
    if (buildId.value) {
      socketStore.socket.emit('joinBuild', buildId.value);
    }
  }
});

onBeforeUnmount(() => {
  // Clean up socket listeners
  if (socketStore.socket) {
    socketStore.socket.off('buildTimeTracking:updated', handleBuildTimeUpdate);
    if (buildId.value) {
      socketStore.socket.emit('leaveBuild', buildId.value);
    }
  }
});
</script>

<template>
  <Dialog
    :visible="props.visible"
    modal
    :style="{ width: '95vw', maxWidth: '900px' }"
    header="Manage Build Positions"
    @update:visible="emit('update:visible', $event)"
  >
    <div
      v-if="props.build"
      class="mb-4 space-y-2"
    >
      <div class="flex flex-wrap gap-x-4 gap-y-2 text-sm text-color-secondary">
        <span class="whitespace-nowrap"><strong>Build ID:</strong> {{ props.build.build_id }}</span>
        <span class="whitespace-nowrap"><strong>Genset:</strong> {{ props.build.genset_number }}</span>
        <span class="whitespace-nowrap"><strong>Track:</strong> {{ props.build.track_id ?? '—' }}</span>
        <span class="min-w-0"><strong>Call In:</strong>
          {{ props.build.call_in_time ? new Date(props.build.call_in_time).toLocaleString('en-CA', { hourCycle: 'h23' }) : '—' }}</span>
        <span class="min-w-0"><strong>Start:</strong>
          {{ props.build.start_time ? new Date(props.build.start_time).toLocaleString('en-CA', { hourCycle: 'h23' }) : '—' }}</span>
      </div>

      <!-- Time Tracking Section - PROMINENT at top -->
      <div
        v-if="!isCompleted"
        class="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-2 border-green-300 dark:border-green-700"
      >
        <div class="flex items-center justify-between flex-wrap gap-3">
          <div class="flex items-center gap-3">
            <span
              v-if="isCurrentUserTracking"
              class="rounded-full bg-green-500 text-white text-xl inline-flex items-center justify-center flex-shrink-0 animate-pulse"
              style="width:44px;height:44px"
            >
              <i class="pi pi-clock" />
            </span>
            <span
              v-else
              class="rounded-full border-2 border-surface-300 bg-white dark:bg-surface-700 text-surface-500 text-xl inline-flex items-center justify-center flex-shrink-0"
              style="width:44px;height:44px"
            >
              <i class="pi pi-clock" />
            </span>

            <div>
              <span class="block text-sm font-semibold text-green-800 dark:text-green-200">Your Build Time</span>
              <span
                v-if="isCurrentUserTracking"
                class="text-2xl font-mono font-bold text-green-600 dark:text-green-400"
              >
                {{ formatElapsedTime(currentUserElapsedSeconds) }}
              </span>
              <span
                v-else
                class="text-sm text-surface-500"
              >Not currently tracking</span>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <Button
              v-if="isCurrentUserTracking"
              label="Sign Out"
              severity="danger"
              icon="pi pi-stop"
              :loading="!!pendingTimerOperation"
              @click="stopBuildTime"
            />
            <Button
              v-else
              label="Sign In"
              severity="success"
              icon="pi pi-play"
              :loading="!!pendingTimerOperation"
              @click="startBuildTime"
            />
            <Button
              icon="pi pi-users"
              severity="secondary"
              outlined
              title="View all workers"
              @click="showTimeTracker = true"
            />
          </div>
        </div>
      </div>

      <!-- Railcar input field -->
      <div
        v-if="!isCompleted && canEdit"
        class="p-3 bg-surface-50 dark:bg-surface-800 rounded border border-surface-200 dark:border-surface-700"
      >
        <label
          for="railcar-input"
          class="block mb-2 text-sm font-medium"
        >
          Genset Railcar Number <span class="text-red-500">*</span>
        </label>
        <div class="flex gap-2">
          <InputText
            id="railcar-input"
            v-model="railcarInput"
            placeholder="Enter railcar number"
            class="flex-1"
            :disabled="savingRailcar"
          />
          <Button
            label="Save"
            icon="pi pi-check"
            :loading="savingRailcar"
            :disabled="!railcarInput.trim() || savingRailcar"
            @click="saveRailcar"
          />
        </div>
        <small
          v-if="!props.build.railcar"
          class="text-orange-600 dark:text-orange-400"
        >
          ⚠ Railcar must be set before completing this build
        </small>
      </div>

      <div
        v-else-if="props.build.railcar"
        class="p-3 bg-surface-50 dark:bg-surface-800 rounded"
      >
        <span class="text-sm"><strong>Railcar:</strong> {{ props.build.railcar }}</span>
      </div>

      <div
        v-else
        class="p-3 bg-orange-50 dark:bg-orange-900/20 rounded border border-orange-200 dark:border-orange-700"
      >
        <span class="text-sm text-orange-700 dark:text-orange-300">⚠ No railcar number set</span>
      </div>

      <!-- Cables and Hooks input fields -->
      <div
        v-if="!isCompleted && canEdit"
        class="p-3 bg-surface-50 dark:bg-surface-800 rounded border border-surface-200 dark:border-surface-700"
      >
        <label class="block mb-2 text-sm font-medium">Equipment Counts</label>
        <div class="flex flex-col sm:flex-row gap-2">
          <div class="flex-1">
            <label
              for="cables-input"
              class="block mb-1 text-xs"
            >Number of Cables</label>
            <InputNumber
              id="cables-input"
              v-model="numCablesInput"
              :min="0"
              :disabled="savingCablesHooks"
              class="w-full"
            />
          </div>
          <div class="flex-1">
            <label
              for="hooks-input"
              class="block mb-1 text-xs"
            >Number of Hooks</label>
            <InputNumber
              id="hooks-input"
              v-model="numHooksInput"
              :min="0"
              :disabled="savingCablesHooks"
              class="w-full"
            />
          </div>
          <div class="w-full sm:w-auto sm:self-end">
            <Button
              label="Save"
              icon="pi pi-check"
              :loading="savingCablesHooks"
              :disabled="savingCablesHooks"
              class="w-full sm:w-auto"
              @click="saveCablesHooks"
            />
          </div>
        </div>
      </div>

      <div
        v-else-if="props.build.num_cables !== null || props.build.num_hooks !== null"
        class="p-3 bg-surface-50 dark:bg-surface-800 rounded"
      >
        <span class="text-sm">
          <strong>Cables:</strong> {{ props.build.num_cables ?? '—' }} |
          <strong>Hooks:</strong> {{ props.build.num_hooks ?? '—' }}
        </span>
      </div>

      <!-- Fuel Level and Engine Hours input fields -->
      <div
        v-if="!isCompleted && canEdit"
        class="p-3 bg-surface-50 dark:bg-surface-800 rounded border border-surface-200 dark:border-surface-700"
      >
        <label class="block mb-2 text-sm font-medium">Fuel & Engine Hours</label>
        <div class="flex flex-col sm:flex-row gap-2">
          <div class="flex-1">
            <label
              for="fuel-level-input"
              class="block mb-1 text-xs"
            >Fuel Level</label>
            <Dropdown
              id="fuel-level-input"
              v-model="fuelLevelInput"
              :options="FUEL_OPTIONS"
              placeholder="Select fuel level"
              :disabled="savingFuelEngineHours"
              class="w-full"
              show-clear
            />
          </div>
          <div class="flex-1">
            <label
              for="g1-hours-input"
              class="block mb-1 text-xs"
            >G1 Engine Hours</label>
            <InputNumber
              id="g1-hours-input"
              v-model="g1HoursInput"
              :min="0"
              :min-fraction-digits="1"
              :max-fraction-digits="1"
              :disabled="savingFuelEngineHours"
              class="w-full"
            />
          </div>
          <div class="flex-1">
            <label
              for="g2-hours-input"
              class="block mb-1 text-xs"
            >G2 Engine Hours</label>
            <InputNumber
              id="g2-hours-input"
              v-model="g2HoursInput"
              :min="0"
              :min-fraction-digits="1"
              :max-fraction-digits="1"
              :disabled="savingFuelEngineHours"
              class="w-full"
            />
          </div>
          <div class="w-full sm:w-auto sm:self-end">
            <Button
              label="Save"
              icon="pi pi-check"
              :loading="savingFuelEngineHours"
              :disabled="savingFuelEngineHours"
              class="w-full sm:w-auto"
              @click="saveFuelEngineHours"
            />
          </div>
        </div>
      </div>

      <div
        v-else-if="props.build.fuel_level || props.build.g1_hours !== null || props.build.g2_hours !== null"
        class="p-3 bg-surface-50 dark:bg-surface-800 rounded"
      >
        <span class="text-sm">
          <strong>Fuel:</strong> {{ props.build.fuel_level ?? '—' }} |
          <strong>G1 Hours:</strong> {{ props.build.g1_hours !== null ? props.build.g1_hours.toFixed(1) : '—' }} |
          <strong>G2 Hours:</strong> {{ props.build.g2_hours !== null ? props.build.g2_hours.toFixed(1) : '—' }}
        </span>
      </div>

      <!-- End Time, Delays, and Repairs input fields -->
      <div
        v-if="!isCompleted && canEdit"
        class="p-3 bg-surface-50 dark:bg-surface-800 rounded border border-surface-200 dark:border-surface-700"
      >
        <label class="block mb-2 text-sm font-medium">Times & Notes</label>
        <div class="flex flex-col gap-3">
          <div class="flex gap-2">
            <div class="flex-1">
              <label
                for="end-time-input"
                class="block mb-1 text-xs"
              >End Time</label>
              <Calendar
                id="end-time-input"
                v-model="endTimeInput"
                show-time
                hour-format="24"
                :disabled="savingTimesNotes"
                placeholder="Select end time"
                class="w-full"
                show-icon
                icon-display="input"
                :show-on-focus="false"
              />
            </div>
          </div>
          <div>
            <label
              for="delays-input"
              class="block mb-1 text-xs"
            >Delays</label>
            <Textarea
              id="delays-input"
              v-model="delaysInput"
              :disabled="savingTimesNotes"
              placeholder="Note any delays encountered..."
              rows="3"
              class="w-full"
            />
          </div>
          <div>
            <label
              for="repairs-input"
              class="block mb-1 text-xs"
            >Repairs Required</label>
            <Textarea
              id="repairs-input"
              v-model="repairsRequiredInput"
              :disabled="savingTimesNotes"
              placeholder="Note any repairs required..."
              rows="2"
              class="w-full"
            />
          </div>
          <div class="flex justify-end">
            <Button
              label="Save"
              icon="pi pi-check"
              :loading="savingTimesNotes"
              :disabled="savingTimesNotes"
              @click="saveTimesNotes"
            />
          </div>
        </div>
      </div>

      <div
        v-else-if="props.build.end_time || props.build.delays || props.build.repairs_required"
        class="p-3 bg-surface-50 dark:bg-surface-800 rounded"
      >
        <div class="text-sm space-y-1">
          <div v-if="props.build.end_time">
            <strong>End Time:</strong> {{ new Date(props.build.end_time).toLocaleString('en-CA', { hourCycle: 'h23' }) }}
          </div>
          <div v-if="props.build.delays">
            <strong>Delays:</strong> {{ props.build.delays }}
          </div>
          <div v-if="props.build.repairs_required">
            <strong>Repairs Required:</strong> {{ props.build.repairs_required }}
          </div>
        </div>
      </div>

      <div
        v-if="isCompleted"
        class="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded"
      >
        <Tag
          severity="success"
          value="COMPLETED"
          icon="pi pi-check-circle"
        />
        <span class="text-sm">
          <strong>Completed:</strong>
          {{ props.build.completed_at ? new Date(props.build.completed_at).toLocaleString('en-CA', { hourCycle: 'h23' }) : '' }}
          <span v-if="props.build.completed_by_first_name">
            by {{ props.build.completed_by_first_name }} {{ props.build.completed_by_last_name }}
          </span>
        </span>
      </div>
    </div>

    <div class="flex flex-col gap-4">
      <fieldset
        :disabled="!canEdit || isCompleted"
        class="border border-surface-300 dark:border-surface-700 rounded p-4"
      >
        <legend class="px-2 text-sm font-medium">
          Quick Add
        </legend>

        <!-- Lock status summary -->
        <div
          v-if="lockedFields.carNumber || lockedFields.well || lockedFields.position"
          class="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-sm"
        >
          <span class="font-medium">🔒 Locked:</span>
          <span
            v-if="lockedFields.carNumber"
            class="ml-2"
          >Car: {{ form.carNumber }}</span>
          <span
            v-if="lockedFields.well"
            class="ml-2"
          >Well: {{ form.well }}</span>
          <span
            v-if="lockedFields.position"
            class="ml-2"
          >Position: {{ form.position }}</span>
        </div>

        <div class="flex flex-wrap gap-3 md:gap-4">
          <div class="w-full sm:flex-1">
            <div class="mb-1 flex items-center min-h-[32px]">
              <label
                class="block text-sm font-medium"
                for="unit-number"
              >Unit Number</label>
            </div>
            <AutoComplete
              id="unit-number"
              v-model="form.unitNumber"
              :suggestions="filteredUnitSuggestions"
              placeholder="e.g. XXXU123456"
              :force-selection="false"
              class="w-full"
              @complete="searchUnits"
            />
          </div>
          <div class="w-full sm:min-w-[200px] sm:flex-1">
            <div class="mb-1 flex items-center justify-between min-h-[32px]">
              <label
                class="block text-sm font-medium"
                for="car-number"
              >Car Number</label>
              <Button
                :icon="lockedFields.carNumber ? 'pi pi-lock' : 'pi pi-lock-open'"
                :severity="lockedFields.carNumber ? 'info' : 'secondary'"
                text
                rounded
                size="small"
                :title="lockedFields.carNumber ? 'Unlock car number' : 'Lock car number'"
                @click="toggleLock('carNumber')"
              />
            </div>
            <InputText
              id="car-number"
              v-model="form.carNumber"
              placeholder="Railcar"
              class="w-full"
            />
          </div>
          <div class="w-full sm:w-auto sm:flex-1">
            <div class="mb-1 flex items-center justify-between min-h-[32px]">
              <label
                class="block text-sm font-medium"
                for="well"
              >Well</label>
              <Button
                :icon="lockedFields.well ? 'pi pi-lock' : 'pi pi-lock-open'"
                :severity="lockedFields.well ? 'info' : 'secondary'"
                text
                rounded
                size="small"
                :title="lockedFields.well ? 'Unlock well' : 'Lock well'"
                @click="toggleLock('well')"
              />
            </div>
            <Dropdown
              id="well"
              v-model="form.well"
              :options="wellOptions"
              placeholder="Select"
              show-clear
              class="w-full"
            />
          </div>
          <div class="w-full sm:w-auto sm:flex-1">
            <div class="mb-1 flex items-center justify-between min-h-[32px]">
              <label
                class="block text-sm font-medium"
                for="position"
              >Position</label>
              <Button
                :icon="lockedFields.position ? 'pi pi-lock' : 'pi pi-lock-open'"
                :severity="lockedFields.position ? 'info' : 'secondary'"
                text
                rounded
                size="small"
                :title="lockedFields.position ? 'Unlock position' : 'Lock position'"
                @click="toggleLock('position')"
              />
            </div>
            <Dropdown
              id="position"
              v-model="form.position"
              :options="positionOptions"
              option-label="label"
              option-value="value"
              class="w-full"
            />
          </div>
          <div class="w-full sm:min-w-[160px] sm:flex-1">
            <div class="mb-1 flex items-center min-h-[32px]">
              <label
                class="block text-sm font-medium"
                for="running-temp-c"
              >Running Temp (°C)</label>
            </div>
            <InputText
              id="running-temp-c"
              v-model="form.runningTempC"
              type="text"
              placeholder="e.g. 2"
              class="w-full"
            />
          </div>
          <div class="w-full sm:min-w-[160px] sm:flex-1">
            <div class="mb-1 flex items-center min-h-[32px]">
              <label
                class="block text-sm font-medium"
                for="billed-setpoint-c"
              >Billed Setpoint (°C)</label>
            </div>
            <InputText
              id="billed-setpoint-c"
              v-model="form.billedSetpointC"
              type="text"
              placeholder="e.g. -18"
              class="w-full"
            />
          </div>
          <div class="w-full sm:min-w-[160px] sm:flex-1">
            <div class="mb-1 flex items-center min-h-[32px]">
              <label
                class="block text-sm font-medium"
                for="actual-setpoint-c"
              >Actual Setpoint (°C)</label>
            </div>
            <InputText
              id="actual-setpoint-c"
              v-model="form.actualSetpointC"
              type="text"
              placeholder="e.g. -18"
              class="w-full"
            />
          </div>
          <div class="w-full sm:flex-1">
            <div class="mb-1 flex items-center min-h-[32px]">
              <label class="block text-sm font-medium">Photo (optional)</label>
            </div>
            <p
              v-if="form.photoUrl"
              class="text-xs text-color-secondary break-all mb-2"
            >
              {{ form.photoUrl }}
            </p>
            <div class="flex flex-col sm:flex-row flex-wrap gap-2">
              <Button
                label="Upload"
                icon="pi pi-upload"
                outlined
                size="small"
                :disabled="!canEdit || uploadingPhoto"
                class="w-full sm:w-auto"
                @click="triggerPhotoSelection('library')"
              />
              <Button
                label="Take Photo"
                icon="pi pi-camera"
                outlined
                size="small"
                :disabled="!canEdit || uploadingPhoto"
                class="w-full sm:w-auto"
                @click="triggerPhotoSelection('camera')"
              />
              <Button
                v-if="form.photoUrl"
                label="Clear"
                icon="pi pi-times"
                size="small"
                severity="secondary"
                class="w-full sm:w-auto"
                @click="form.photoUrl = ''"
              />
            </div>
            <p
              v-if="uploadingPhoto"
              class="text-xs text-color-secondary mt-2"
            >
              Uploading photo…
            </p>
          </div>
        </div>
        <div class="flex justify-end mt-4">
          <Button
            label="Add Position"
            icon="pi pi-plus"
            :loading="submitting"
            :disabled="submitting || !canEdit"
            @click="addPosition"
          />
        </div>
      </fieldset>

      <input
        ref="libraryFileInput"
        type="file"
        accept="image/*"
        class="hidden"
        @change="handlePhotoChange('library', $event)"
      >
      <input
        ref="cameraFileInput"
        type="file"
        accept="image/*"
        capture="environment"
        class="hidden"
        @change="handlePhotoChange('camera', $event)"
      >

      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h3 class="text-lg font-medium m-0">
          Recorded Positions ({{ positions.length }})
        </h3>
        <div class="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            v-if="!isCompleted && canEdit && positions.length > 0"
            label="Mark Build Complete & Send Report"
            icon="pi pi-check-circle"
            severity="success"
            :loading="completingBuild"
            :disabled="completingBuild"
            class="w-full sm:w-auto"
            @click="handleCompleteBuild"
          />
          <div
            v-if="positions.length > 0"
            class="flex gap-2 flex-wrap"
          >
            <Button
              label="Download New Format"
              icon="pi pi-download"
              severity="secondary"
              outlined
              :loading="downloadingNew"
              :disabled="downloadingNew || downloadingOriginal"
              size="small"
              class="flex-1 sm:flex-initial"
              @click="handleDownloadReport('new')"
            />
            <Button
              label="Download Original Format"
              icon="pi pi-download"
              severity="secondary"
              outlined
              :loading="downloadingOriginal"
              :disabled="downloadingNew || downloadingOriginal"
              size="small"
              class="flex-1 sm:flex-initial"
              @click="handleDownloadReport('original')"
            />
          </div>
        </div>
      </div>

      <DataTable
        :value="positions"
        :loading="loadingPositions"
        data-key="position_id"
        size="small"
        responsive-layout="scroll"
        empty-message="No positions recorded yet."
        :reorderable-rows="canEdit && !isCompleted"
        @row-reorder="handleRowReorder"
      >
        <Column
          v-if="canEdit && !isCompleted"
          :row-reorder="true"
          style="width: 3rem"
        />
        <Column
          field="sequence"
          header="#"
          style="width: 4rem"
        >
          <template #body="slotProps">
            <span class="font-semibold text-gray-500">{{ slotProps.data.sequence }}</span>
          </template>
        </Column>
        <Column
          field="unit_number"
          header="Unit"
        />
        <Column
          field="car_number"
          header="Car"
        />
        <Column
          field="well"
          header="Well"
        />
        <Column
          field="position"
          header="Position"
        >
          <template #body="slotProps">
            <Tag :value="slotProps.data.position" />
          </template>
        </Column>
        <Column header="Flags">
          <template #body="slotProps">
            <div class="flex gap-1 flex-wrap">
              <Tag
                v-if="slotProps.data.out_of_range_flag"
                severity="danger"
                value="OUT OF RANGE"
              />
              <Tag
                v-if="slotProps.data.setpoint_discrepancy_flag"
                severity="warning"
                value="SETPOINT MISMATCH"
              />
            </div>
          </template>
        </Column>
        <Column
          field="created_at"
          header="Recorded"
        >
          <template #body="slotProps">
            {{ formatRecordedAt(slotProps.data.created_at) }}
          </template>
        </Column>
        <Column header="Recorder">
          <template #body="slotProps">
            <span v-if="slotProps.data.created_by_first_name">
              {{ slotProps.data.created_by_first_name }} {{ slotProps.data.created_by_last_name }}
            </span>
            <span v-else>—</span>
          </template>
        </Column>
        <Column
          v-if="canEdit && !isCompleted"
          header="Actions"
          style="width: 6rem"
        >
          <template #body="slotProps">
            <Button
              icon="pi pi-trash"
              text
              severity="danger"
              :loading="deletingId !== null && deletingId === Number(slotProps.data.position_id)"
              @click="removePosition(slotProps.data.position_id, $event)"
            />
          </template>
        </Column>
      </DataTable>

      <!-- Time Records Section - Only visible to managers/admins/supervisors/dispatchers -->
      <div
        v-if="canManageTimeRecords"
        class="mt-6 pt-4 border-t border-surface-200 dark:border-surface-700"
      >
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
          <h3 class="text-lg font-medium m-0">
            Time Records ({{ timeRecords.length }})
          </h3>
          <Button
            v-if="!isCompleted"
            label="Add Time Record"
            icon="pi pi-plus"
            severity="secondary"
            size="small"
            @click="addTimeRecordDialogVisible = true"
          />
        </div>

        <DataTable
          :value="timeRecords"
          :loading="loadingTimeRecords"
          data-key="id"
          size="small"
          responsive-layout="scroll"
          empty-message="No time records found."
        >
          <Column
            header="Worker"
            style="min-width: 150px"
          >
            <template #body="slotProps">
              {{ slotProps.data.first_name }} {{ slotProps.data.last_name }}
            </template>
          </Column>
          <Column
            header="Start Time"
            style="min-width: 160px"
          >
            <template #body="slotProps">
              {{ formatDateTime(slotProps.data.start_time) }}
            </template>
          </Column>
          <Column
            header="End Time"
            style="min-width: 160px"
          >
            <template #body="slotProps">
              <span :class="{ 'text-green-600 dark:text-green-400 font-medium': !slotProps.data.end_time }">
                {{ formatDateTime(slotProps.data.end_time) }}
              </span>
            </template>
          </Column>
          <Column
            header="Duration"
            style="min-width: 100px"
          >
            <template #body="slotProps">
              <span class="font-mono">
                {{ formatDuration(slotProps.data.start_time, slotProps.data.end_time) }}
              </span>
            </template>
          </Column>
          <Column
            header="Actions"
            style="width: 100px"
          >
            <template #body="slotProps">
              <div class="flex gap-1">
                <Button
                  icon="pi pi-pencil"
                  text
                  severity="secondary"
                  size="small"
                  title="Edit time record"
                  @click="handleEditTimeRecord(slotProps.data)"
                />
                <Button
                  v-if="slotProps.data.end_time"
                  icon="pi pi-trash"
                  text
                  severity="danger"
                  size="small"
                  title="Delete time record"
                  :loading="deletingTimeRecordId === slotProps.data.id"
                  @click="handleDeleteTimeRecord(slotProps.data, $event)"
                />
              </div>
            </template>
          </Column>
          <template #footer>
            <div class="flex justify-end font-medium">
              <span>Total Time: <span class="font-mono">{{ formattedTotalTime }}</span></span>
            </div>
          </template>
        </DataTable>
      </div>
    </div>

    <!-- Bulk Add dialog disabled - Quick Add with sticky fields is sufficient -->
    <!-- <BatchAddBuildPositionsDialog
      v-model:visible="bulkDialogVisible"
      v-if="bulkDialogVisible && buildId"
      :build-id="buildId"
      @positions-added="handleBulkAdded"
    /> -->
  </Dialog>

  <!-- Build Time Tracker Slideover -->
  <BuildTimeTrackerSlideover
    v-if="buildId"
    v-model:visible="showTimeTracker"
    :build-id="buildId"
    :genset-number="props.build?.genset_number"
  />

  <!-- Edit Time Record Dialog -->
  <EditBuildTimeRecordDialog
    v-model:visible="editTimeRecordDialogVisible"
    :time-record="selectedTimeRecord"
    @refresh-times="refreshTimeRecords"
  />

  <!-- Add Time Record Dialog -->
  <AddBuildTimeRecordDialog
    v-if="buildId"
    v-model:visible="addTimeRecordDialogVisible"
    :build-id="buildId"
    @refresh-times="refreshTimeRecords"
  />
</template>
